import {
  createContext,
  ReactNode,
  useContext,
  useMemo,
  useState,
} from "react";
import { AuthContextType } from "../model/AuthContextType";
import { Role, UserProfile } from "../model/types";

const STORAGE_KEY = "food-ordering-auth";

type StoredAuth = {
  token: string;
  user: UserProfile;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const readStoredAuth = (): StoredAuth | null => {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as StoredAuth;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
};

export const getStoredToken = () => readStoredAuth()?.token ?? null;

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const storedAuth = readStoredAuth();
  const [token, setToken] = useState<string | null>(storedAuth?.token ?? null);
  const [user, setUser] = useState<UserProfile | null>(storedAuth?.user ?? null);

  const login = (nextToken: string, nextUser: UserProfile) => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ token: nextToken, user: nextUser })
    );
    setToken(nextToken);
    setUser(nextUser);
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setToken(null);
    setUser(null);
  };

  const value = useMemo<AuthContextType>(
    () => ({
      isAuthenticated: Boolean(token && user),
      token,
      user,
      login,
      logout,
      hasRole: (roles: Role[]) => Boolean(user && roles.includes(user.role)),
    }),
    [token, user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("Auth context is unavailable");
  }
  return context;
};
